import { useState, useEffect, useCallback } from 'react';
import seoOpportunitiesService from '@/services/seoOpportunities';
import seoAiService, {
  SerpSummary,
  SerpAnalysisResult,
  AnalyzedOpportunity,
  LivePositionResult,
} from '@/services/seoAi';
import wordpressService from '@/services/wordpress';
import {
  SeoKeywordOpportunity,
  OpportunityStatus,
  FixGuidance,
  AiSuggestion,
  SeoProject,
} from '@/types/api/seo';

type AnalysisParams = {
  keyword: string;
  position: number | null;
  ctr: number | null;
  clicks: number | null;
  impressions: number | null;
  days: number;
  rankingPages: Array<{ page: string; position: number; clicks: number; impressions: number; ctr: number }>;
  serpSummary: SerpSummary | null;
  serpAnalysis?: SerpAnalysisResult | null;
  positionHistory: Array<{ date: string; position: number }>;
  rankingPageUrl?: string;
  sitePages?: Array<{ page: string; keyword?: string }>;
  livePosition?: LivePositionResult | null;
  project?: SeoProject;
  projectId: string;
  keywordId: string;
};

export function useOpportunityAnalysis(params: AnalysisParams) {
  const {
    keyword, position, ctr, clicks, impressions, days, rankingPages,
    serpSummary, serpAnalysis, positionHistory, rankingPageUrl, sitePages,
    livePosition, project, projectId, keywordId,
  } = params;

  const [opportunities, setOpportunities] = useState<SeoKeywordOpportunity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load persisted opportunities
  useEffect(() => {
    if (!keywordId || loaded) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await seoOpportunitiesService.getByKeyword(keywordId);
        setOpportunities(data);
      } catch {
        /* ignore */
      }
      setIsLoading(false);
      setLoaded(true);
    };
    load();
  }, [keywordId, loaded]);

  // Snapshot current metrics for active opportunities
  useEffect(() => {
    if (!opportunities.length || position == null) return;
    const active = opportunities.filter((o) => o.status === 'open' || o.status === 'in_progress');
    for (const opp of active) {
      seoOpportunitiesService.upsertTodaySnapshot(opp.id, { position, ctr, clicks, impressions });
    }
  }, [opportunities, position, ctr, clicks, impressions]);

  const handleAnalyze = useCallback(async () => {
    setIsAnalyzing(true);
    try {
      // Step 1: Ensure we have live position data
      let livePosData = livePosition ?? null;
      if (!livePosData && keyword && project?.siteUrl) {
        try {
          livePosData = await seoAiService.checkLivePosition({ keyword, siteUrl: project.siteUrl });
        } catch {
          /* continue without live position */
        }
      }

      // Step 2: Ensure we have SERP competitor data
      let currentSerpSummary = serpSummary;
      let currentSerpAnalysis = serpAnalysis;
      if (!currentSerpSummary && keyword) {
        try {
          const serpResult = await seoAiService.analyzeSerpCompetitors({
            keyword,
            competitorUrls: rankingPages.map((p) => p.page),
            userSiteUrl: project?.siteUrl ?? undefined,
          });
          currentSerpSummary = serpResult.summary;
          currentSerpAnalysis = serpResult;
        } catch {
          /* continue without SERP data */
        }
      }

      const effectivePosition = livePosData?.livePosition ?? position;

      const result = await seoAiService.analyzeOpportunities({
        keyword,
        position: effectivePosition,
        ctr,
        clicks,
        impressions,
        days,
        rankingPages,
        serpSummary: currentSerpSummary,
        positionHistory,
        rankingPageUrl: rankingPageUrl ?? null,
        sitePages: sitePages ?? [],
        livePosition: livePosData ?? undefined,
      });

      const aiOpps = result.opportunities ?? [];

      await seoOpportunitiesService.bulkUpsert(
        projectId,
        keywordId,
        aiOpps.map((o: AnalyzedOpportunity) => ({
          opportunityType: o.opportunityType,
          priority: o.priority,
          title: o.title,
          description: o.description,
          metric: o.metric,
          impactScore: o.impactScore,
          aiFixGuidance: o.fixGuidance,
          detectedPosition: position,
          detectedCtr: ctr,
          detectedClicks: clicks,
          detectedImpressions: impressions,
          isAiGenerated: true,
        }))
      );

      const all = await seoOpportunitiesService.getByKeyword(keywordId);
      setOpportunities(all);
    } catch (err) {
      console.error('Failed to analyze opportunities:', err);
    }
    setIsAnalyzing(false);
  }, [keyword, position, ctr, clicks, impressions, days, rankingPages, serpSummary, serpAnalysis, positionHistory, projectId, keywordId, project, livePosition, rankingPageUrl, sitePages]);

  const handleStatusChange = useCallback(async (id: string, status: OpportunityStatus) => {
    try {
      await seoOpportunitiesService.updateStatus(id, status);
      setOpportunities((prev) =>
        prev
          .map((o) =>
            o.id === id
              ? { ...o, status, updatedAt: new Date().toISOString(), resolvedAt: status === 'completed' ? new Date().toISOString() : o.resolvedAt }
              : o
          )
          .filter((o) => status !== 'dismissed' || o.id !== id)
      );
    } catch {
      /* ignore */
    }
  }, []);

  const handleGetFixGuidance = useCallback(
    async (opportunity: SeoKeywordOpportunity): Promise<FixGuidance | null> => {
      try {
        const guidance = await seoAiService.getFixGuidance({
          keyword,
          opportunityType: opportunity.opportunityType,
          opportunityTitle: opportunity.title,
          position,
          ctr,
          clicks,
          impressions,
          serpSummary,
        });
        await seoOpportunitiesService.saveFixGuidance(opportunity.id, guidance);
        setOpportunities((prev) => prev.map((o) => (o.id === opportunity.id ? { ...o, aiFixGuidance: guidance } : o)));
        return guidance;
      } catch {
        return null;
      }
    },
    [keyword, position, ctr, clicks, impressions, serpSummary]
  );

  const handleGetSuggestions = useCallback(
    async (opportunity: SeoKeywordOpportunity): Promise<AiSuggestion[] | null> => {
      try {
        const result = await seoAiService.getOpportunitySuggestions({
          keyword,
          opportunityType: opportunity.opportunityType,
          opportunityTitle: opportunity.title,
          opportunityDescription: opportunity.description,
          position,
          ctr,
          clicks,
          impressions,
          serpSummary,
          pageUrl: rankingPageUrl,
          evidence: (opportunity as any).evidence ?? undefined,
          competitors: serpAnalysis?.competitors ?? undefined,
          peopleAlsoAsk: serpAnalysis?._serpFeatures?.peopleAlsoAsk ?? undefined,
          relatedSearches: serpAnalysis?._serpFeatures?.relatedSearches ?? undefined,
        });
        const suggestions = result.suggestions ?? [];
        await seoOpportunitiesService.saveSuggestions(opportunity.id, suggestions);
        setOpportunities((prev) => prev.map((o) => (o.id === opportunity.id ? { ...o, aiSuggestions: suggestions } : o)));
        return suggestions;
      } catch {
        return null;
      }
    },
    [keyword, position, ctr, clicks, impressions, serpSummary, rankingPageUrl, serpAnalysis]
  );

  const handleApplyToWordPress = useCallback(
    async (opportunityId: string, suggestion: AiSuggestion, pageUrl: string | null): Promise<{ success: boolean; error?: string }> => {
      if (!project || !pageUrl) return { success: false, error: 'No page URL available' };

      try {
        let postId: number | null = null;
        let postType = 'pages';

        const { data: pagesData } = await import('@/lib/supabase').then((m) =>
          m.supabase
            .from('seo_pages')
            .select('wp_post_id, wp_post_type')
            .eq('project_id', project.id)
            .eq('url', pageUrl)
            .not('wp_post_id', 'is', null)
            .limit(1)
            .maybeSingle()
        );

        if (pagesData?.wp_post_id) {
          postId = pagesData.wp_post_id;
          postType = pagesData.wp_post_type === 'post' ? 'posts' : 'pages';
        } else {
          const post = await wordpressService.findPostByUrl(project.id, pageUrl);
          if (!post) return { success: false, error: `Page not found in WordPress for: ${pageUrl}` };
          postId = post.postId;
          postType = post.postType;
        }

        if (!postId) return { success: false, error: `Page not found in WordPress for: ${pageUrl}` };

        const changes: Record<string, string> = {};
        let actionType = 'custom_note';

        if (suggestion.type === 'title_tag') {
          changes.seoTitle = suggestion.value;
          actionType = 'title_updated';
        } else if (suggestion.type === 'meta_description') {
          changes.seoDescription = suggestion.value;
          actionType = 'meta_updated';
        } else if (suggestion.type === 'h2_section' || suggestion.type === 'paragraph' || suggestion.type === 'faq_entry') {
          changes.content = suggestion.value;
          actionType = 'content_added';
        }

        if (Object.keys(changes).length === 0) {
          return { success: false, error: 'This suggestion type cannot be applied to WordPress' };
        }

        const result = await wordpressService.applyChanges({
          projectId: project.id,
          postId,
          postType,
          opportunityId,
          pageUrl,
          keywordId,
          beforeMetrics: { position, ctr, clicks, impressions },
          changes,
        });

        return result;
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to apply' };
      }
    },
    [project, keywordId, position, ctr, clicks, impressions]
  );

  const stepToOpportunityType: Record<string, string> = {
    search_intent: 'search_intent_mismatch',
    content_gap: 'content_gap',
    competitor_depth: 'content_depth',
    title_meta: 'title_meta_optimization',
    content_upgrade: 'content_depth',
    structure_schema: 'featured_snippet',
    technical: 'technical_seo',
    internal_links: 'internal_linking',
    cannibalization: 'cannibalization',
    position_tracking: 'quick_win',
    content_freshness: 'content_freshness',
  };

  const handleGenerateStepSuggestions = useCallback(
    async (stepId: string, stepLabel: string, stepDescription: string) => {
      try {
        const oppType = stepToOpportunityType[stepId] ?? 'custom';
        const opp = await seoOpportunitiesService.upsert({
          projectId,
          keywordId,
          opportunityType: oppType,
          priority: 'medium',
          title: `Improve: ${stepLabel}`,
          description: `${stepDescription} — No specific issues detected by automated checks, but this area likely needs improvement to reach the top 3.`,
          metric: 'Needs improvement to reach top 3',
          impactScore: 50,
          isAiGenerated: false,
          detectedPosition: livePosition?.livePosition ?? position,
          detectedCtr: ctr,
          detectedClicks: clicks,
          detectedImpressions: impressions,
        });

        const result = await seoAiService.getOpportunitySuggestions({
          keyword,
          opportunityType: oppType,
          opportunityTitle: `Improve: ${stepLabel}`,
          opportunityDescription: stepDescription,
          position: livePosition?.livePosition ?? position,
          ctr,
          clicks,
          impressions,
          serpSummary,
          pageUrl: rankingPageUrl,
          competitors: serpAnalysis?.competitors ?? undefined,
          peopleAlsoAsk: serpAnalysis?._serpFeatures?.peopleAlsoAsk ?? undefined,
          relatedSearches: serpAnalysis?._serpFeatures?.relatedSearches ?? undefined,
        });

        const suggestions = result.suggestions ?? [];
        if (suggestions.length > 0) {
          await seoOpportunitiesService.saveSuggestions(opp.id, suggestions);
          opp.aiSuggestions = suggestions;
        }

        const all = await seoOpportunitiesService.getByKeyword(keywordId);
        setOpportunities(all);
      } catch (err) {
        console.error('Failed to generate step suggestions:', err);
      }
    },
    [projectId, keywordId, keyword, position, ctr, clicks, impressions, serpSummary, rankingPageUrl, serpAnalysis, livePosition]
  );

  return {
    opportunities,
    setOpportunities,
    isLoading,
    isAnalyzing,
    handleAnalyze,
    handleStatusChange,
    handleGetFixGuidance,
    handleGetSuggestions,
    handleApplyToWordPress,
    handleGenerateStepSuggestions,
  };
}
